import { VideoEvent } from "../client";
import { getTargetPlayerSlot } from "./player";

/**
 * Returns true for tag values that should be hidden from the UI
 * (empty, unknown, placeholder, or non-descriptive values).
 */
export const isUnknownTag = (tag: unknown): boolean => {
  if (typeof tag !== "string") return true;
  const trimmed = tag.trim().toLowerCase();
  return (
    !trimmed ||
    trimmed.includes("unknown") ||
    trimmed.includes("unkown") ||
    trimmed === "[object object]" ||
    trimmed === "2pt"
  );
};

/**
 * Derives a list of user-friendly display tags for a video event.
 *
 * Priority: valid raw tags → description-inferred tags → pta fallback.
 * When a target player is the assister / stealer / blocker, the leading
 * tag is re-labelled accordingly.
 */
export const getDisplayTags = (
  video: VideoEvent,
  targetPlayerId?: string | null,
  targetPlayerName?: string | null
): string[] => {
  const rawTags = Array.isArray(video.tags) ? video.tags : [];
  let valid = rawTags.filter((t) => !isUnknownTag(t));

  if (valid.length === 0) {
    if (video.desc) {
      const desc = video.desc;
      const distMatch = desc.match(/(?:\b|\s)(\d+)'(?:\s|$)/);
      if (distMatch) valid.push(`${distMatch[1]} ft`);
      if (/\b3(?:pt|-?pointer)\b/i.test(desc)) valid.push("3pt");
      else if (/dunk/i.test(desc)) valid.push("Dunk");
      else if (/layup/i.test(desc)) valid.push("Layup");
      else if (/hook/i.test(desc)) valid.push("Hook Shot");
      else if (/jump\s+shot/i.test(desc)) valid.push("Jump Shot");
      else if (/rebound/i.test(desc)) valid.push("Rebound");
      else if (/turnover/i.test(desc)) valid.push("Turnover");
      else if (/foul/i.test(desc)) valid.push("Foul");
      else if (/free\s+throw/i.test(desc)) valid.push("FT");
    }

    if (valid.length === 0 && video.pta && !isUnknownTag(video.pta)) {
      valid = [video.pta];
    }
  }

  const slot = getTargetPlayerSlot(video, targetPlayerId, targetPlayerName);
  const desc = video.desc ? video.desc.toLowerCase() : "";

  if (slot === 2) {
    if (valid.some((t) => t.toLowerCase() === "make") || desc.includes(" ast)")) {
      const withoutMake = valid.filter((t) => t.toLowerCase() !== "make");
      return ["Assist", ...withoutMake];
    }
    if (
      valid.some((t) => t.toLowerCase() === "turnover") ||
      desc.includes("steal") ||
      desc.includes("stl")
    ) {
      const withoutTurnover = valid.filter((t) => t.toLowerCase() !== "turnover");
      return ["Steal", ...withoutTurnover];
    }
    if (valid.some((t) => t.toLowerCase() === "foul")) {
      const withoutFoul = valid.filter((t) => t.toLowerCase() !== "foul");
      return ["Foul Drawn", ...withoutFoul];
    }
  }

  if (slot === 3) {
    const withoutMiss = valid.filter((t) => t.toLowerCase() !== "miss");
    return ["Block", ...withoutMiss];
  }

  return valid;
};

/** Sort-order weight for a tag string (lower = earlier). */
export const TAG_ORDER = (tag: string): number => {
  const lower = tag.trim().toLowerCase();
  // Main stat
  if (
    [
      "make",
      "miss",
      "assist",
      "block",
      "steal",
      "foul drawn",
      "rebound",
      "turnover",
      "foul",
      "ft",
    ].includes(lower)
  )
    return 0;
  // Type
  if (
    lower.includes("3pt") ||
    lower.includes("dunk") ||
    lower.includes("layup") ||
    lower.includes("hook shot") ||
    lower.includes("jump shot") ||
    lower.includes("free throw")
  )
    return 1;
  // Shot distance
  if (/^\d+ ft$/.test(tag.trim())) return 3;
  // Sub type
  if (
    lower.includes("step back") ||
    lower.includes("fadeaway") ||
    lower.includes("pullup") ||
    lower.includes("floating")
  )
    return 2;
  // Everything else
  return 4;
};

/** Sort display tags by category weight. */
export const sortDisplayTags = (tags: string[]): string[] =>
  [...tags].sort((a, b) => TAG_ORDER(a) - TAG_ORDER(b));

/** Return Mantine Badge `color` and `variant` for a given tag string. */
export const getBadgeStyles = (
  tag: string
): { color: string; variant: "filled" | "outline" } => {
  const lower = tag.trim().toLowerCase();
  if (lower === "assist") return { color: "cyan", variant: "filled" };
  if (lower === "block") return { color: "grape", variant: "filled" };
  if (lower === "steal") return { color: "lime", variant: "filled" };
  if (lower === "make") return { color: "teal", variant: "filled" };
  if (lower === "miss") return { color: "red.7", variant: "filled" };
  if (lower === "3pt" || lower.includes("3pt"))
    return { color: "blue", variant: "outline" };
  if (lower === "ft" || lower.includes("free throw") || lower.includes("freethrow"))
    return { color: "cyan", variant: "outline" };
  if (lower === "rebound") return { color: "indigo", variant: "filled" };
  if (lower === "turnover") return { color: "orange", variant: "filled" };
  if (lower === "foul" || lower === "foul drawn")
    return { color: "yellow.8", variant: "outline" };
  if (lower.includes("dunk")) return { color: "grape", variant: "outline" };
  if (lower.includes("layup")) return { color: "green", variant: "outline" };
  if (
    lower.includes("jump shot") ||
    lower.includes("hook shot") ||
    lower.includes("jumper") ||
    lower.includes("step back") ||
    lower.includes("fadeaway") ||
    lower.includes("pullup") ||
    lower.includes("floating")
  )
    return { color: "yellow.7", variant: "outline" };
  if (/^\d+ ft$/.test(tag.trim())) return { color: "gray.4", variant: "outline" };
  return { color: "gray.4", variant: "outline" };
};
